import { createSlice } from '@reduxjs/toolkit';
import type { Draft } from '@reduxjs/toolkit';

import { SliceSelectors } from 'state/meter-type/meter-type-slice';

import type { TMeter } from 'api/models/TMeter';
import type { NormalizedError } from 'errors/types';
import type { RootState } from 'state/store';
import { TAddress } from 'api/models/TAddress';

export type MeterPointValidationState = {
  validationRequired: {
    electricity: boolean | null;
    gas: boolean | null;
  };
  userMeterpoints: {
    MPRN: string | null;
    MPAN: string | null;
  };
  data: TMeter[];
  isLoading: boolean;
  errors: NormalizedError[] | null;
};

function upsertMeter(
  state: Draft<MeterPointValidationState>,
  meter: TMeter | undefined
) {
  if (!meter) return;
  const idx = state.data.findIndex(
    ({ fuelType }) => fuelType === meter.fuelType
  );
  idx >= 0 ? (state.data[idx] = meter) : state.data.push(meter);
}

function matchesFuelType(e: NormalizedError, fuelType: string) {
  return e.keyword === fuelType || e.fuelType === fuelType;
}

function mergeErrors(
  state: Draft<MeterPointValidationState>,
  incoming: NormalizedError[],
  fuelType: string
) {
  const existing = (state.errors ?? []).filter(
    (e) => !matchesFuelType(e, fuelType)
  );
  const filtered = incoming.filter((e) => matchesFuelType(e, fuelType));
  state.errors = [...existing, ...filtered];
}

export const initialState: MeterPointValidationState = {
  validationRequired: {
    electricity: null,
    gas: null
  },
  userMeterpoints: {
    MPRN: null,
    MPAN: null
  },
  data: [],
  isLoading: false,
  errors: null
};

export const slice = createSlice({
  name: 'meterpointValidation',
  initialState,
  reducers: {
    setInitialState: (state) => {
      Object.entries(initialState).forEach(
        ([key, value]) => (state[key] = value)
      );
    },
    setMPRN: (state, { payload }) => {
      state.userMeterpoints.MPRN = payload;
    },
    setMPAN: (state, { payload }) => {
      state.userMeterpoints.MPAN = payload;
    },
    removeMPRN: (state) => {
      state.userMeterpoints.MPRN = initialState.userMeterpoints.MPRN;
      state.data = state.data.filter(({ fuelType }) => fuelType !== 'Gas');
    },
    removeMPAN: (state) => {
      state.userMeterpoints.MPAN = initialState.userMeterpoints.MPAN;
      state.data = state.data.filter(
        ({ fuelType }) => fuelType !== 'Electricity'
      );
    },
    setValidationRequired: (state, { payload }) => {
      state.validationRequired = payload;
    },
    resetData: (state) => {
      state.data = initialState.data;
    },
    resetErrors: (state) => {
      state.errors = initialState.errors;
    },
    setIsLoading: (state, { payload }) => {
      const { mpan, mprn, releaseSearchByMpxnVoiceSales } =
        payload.params ?? {};

      const mpanOnly = mpan && !mprn;
      const mprnOnly = mprn && !mpan;

      state.isLoading = true;

      switch (true) {
        case releaseSearchByMpxnVoiceSales && mpanOnly:
          mergeErrors(state, [], 'Electricity');
          break;
        case releaseSearchByMpxnVoiceSales && mprnOnly:
          mergeErrors(state, [], 'Gas');
          break;
        default:
          state.errors = initialState.errors;
      }
    },
    setSuccess: (state, { payload }) => {
      const { releaseSearchByMpxnVoiceSales } = payload;
      const { mpan, mprn } = payload.params ?? {};
      const mpanOnly = mpan && !mprn;
      const mprnOnly = mprn && !mpan;

      state.isLoading = false;
      switch (true) {
        case releaseSearchByMpxnVoiceSales && mpanOnly:
          upsertMeter(
            state,
            payload.meterpoints.find(
              ({ fuelType }) => fuelType === 'Electricity'
            )
          );
          mergeErrors(state, payload.errors, 'Electricity');
          break;
        case releaseSearchByMpxnVoiceSales && mprnOnly:
          upsertMeter(
            state,
            payload.meterpoints.find(({ fuelType }) => fuelType === 'Gas')
          );
          mergeErrors(state, payload.errors, 'Gas');
          break;
        default:
          state.data = payload.meterpoints;
          state.errors = payload.errors;
      }
    },
    setError: (state, { payload }) => {
      state.isLoading = false;
      state.errors = payload;
    }
  }
});

export const {
  setInitialState,
  setMPRN,
  setMPAN,
  removeMPAN,
  removeMPRN,
  setValidationRequired,
  setIsLoading,
  setSuccess,
  setError,
  resetData,
  resetErrors
} = slice.actions;

const defaultSelectors = new SliceSelectors(slice.name);

const {
  selectSlice,
  selectGasMeter,
  selectElectricityMeter
} = defaultSelectors;

export const selectUserMeterpoints = (
  state: RootState
): MeterPointValidationState['userMeterpoints'] =>
  selectSlice(state).userMeterpoints;

export const selectValidationRequired = (
  state: RootState
): MeterPointValidationState['validationRequired'] =>
  selectSlice(state).validationRequired;

export const selectMeterAddresses = (
  state: RootState
): {
  electricity?: TAddress;
  gas?: TAddress;
} => {
  const { electricity, gas } = selectValidationRequired(state);
  const electricityMeter = selectElectricityMeter(state);
  const gasMeter = selectGasMeter(state);

  return {
    ...(electricity && {
      electricity: electricityMeter ? electricityMeter.address : null
    }),
    ...(gas && { gas: gasMeter ? gasMeter.address : null })
  };
};
export const selectValidationErrors = (state: RootState) => {
  const errors = defaultSelectors.selectErrors(state) || [];

  return errors.filter(
    (error) => error && error.keyword?.toLowerCase() === 'validation'
  );
};

export const selectors = {
  ...defaultSelectors,
  selectUserMeterpoints,
  selectValidationRequired,
  selectMeterAddresses,
  selectValidationErrors
};

export default slice.reducer;
